/**
 * FEA-309 -- passcode input helpers, kept as pure functions so they can be tested without a DOM.
 *
 * The paste case is the one that earns its own module. Nobody types a six-digit code they can copy,
 * and what actually lands on the clipboard out of an email client is rarely the bare code: it comes
 * with a trailing newline, leading spaces from a table cell, a non-breaking space, or the sentence
 * around it.
 */

export const CODE_LENGTH = 6

/**
 * Pull a passcode out of whatever was pasted.
 *
 * Prefer a standalone run of exactly CODE_LENGTH digits; only if there isn't one, fall back to
 * concatenating every digit and taking the first CODE_LENGTH.
 *
 * The fallback alone is not enough, and the case that breaks it is one we write ourselves: the
 * passcode screen — and the email — say the code "expires in 10 minutes", so
 * "Expires in 10 minutes. Code: 123456" is an entirely ordinary thing to paste. Concatenating
 * digits turns that into "101234" and the admin is told their correct code is wrong.
 *
 * Splitting on non-digits handles the rest for free: spaces, tabs, newlines, the U+00A0 an email
 * client's table cell hands over and the U+200B a tracking wrapper adds are all non-digits, so
 * "123 456" still joins back to "123456".
 *
 * Returns '' when there is nothing usable, so the caller can leave the field untouched rather than
 * clearing a code the admin already typed.
 */
export function normalizePastedCode(text, length = CODE_LENGTH) {
  if (text === null || text === undefined) return ''

  // No lookbehind: CRA 3's browserslist still includes Safari versions that do not support it.
  const groups = String(text).split(/\D+/).filter(Boolean)

  const standalone = groups.find(group => group.length === length)
  if (standalone) return standalone

  return groups.join('').slice(0, length)
}

/**
 * Spread a normalized code across the individual boxes, padding with '' so React keeps every input
 * controlled. A short paste fills what it can and leaves the rest blank.
 */
export function digitsToBoxes(code, length = CODE_LENGTH) {
  const digits = normalizePastedCode(code, length).split('')
  return Array.from({ length }, (_, i) => digits[i] || '')
}

export function boxesToCode(boxes) {
  return (boxes || []).join('')
}

export function isComplete(boxes, length = CODE_LENGTH) {
  return boxesToCode(boxes).length === length
}

/**
 * A single keystroke into one box. Non-digits are dropped rather than rendered, and a multi-character
 * value (some mobile keyboards and autofill deliver the whole code into the focused box) is treated
 * as a paste starting at that box instead of being truncated to one character.
 */
export function applyBoxInput(boxes, index, rawValue, length = CODE_LENGTH) {
  const digits = normalizePastedCode(rawValue, length)
  const next = [...boxes]

  if (digits.length === 0) {
    next[index] = ''
    return { boxes: next, focus: index }
  }

  for (let i = 0; i < digits.length && index + i < length; i++) {
    next[index + i] = digits[i]
  }

  return { boxes: next, focus: Math.min(index + digits.length, length - 1) }
}
